// src/features/privateAlbum/components/AlbumManager.jsx

import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Stack,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  UploadFile as UploadIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Comment as CommentIcon,
} from "@mui/icons-material";
import { formatDate, formatDateTime } from "../utils/privateAlbumHelpers";
import { getAlbumStatusDisplay } from "../utils/privateAlbumHelpers";
import PhotoGrid from "./PhotoGrid";

const AlbumManager = ({
  album,
  photos,
  onEdit,
  onDelete,
  onArchive,
  onRestore,
  onUpload,
  onShare,
  onPhotoDelete,
  onPhotoUpdate,
  onSetCover,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!album) return null;

  const statusDisplay = getAlbumStatusDisplay(album.status);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
      {/* Album Header */}
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-start" }}
          spacing={2}
        >
          <Box flex={1}>
            <Typography variant="h4" gutterBottom>
              {album.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {album.description}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 1 }}
            >
              <Chip
                icon={<PhotoIcon />}
                label={`${album.totalPhotos || 0} photos`}
                size="small"
              />
              <Chip label={`👤 ${album.clientNames}`} size="small" />
              <Chip label={`📅 ${formatDate(album.eventDate)}`} size="small" />
              {album.location && (
                <Chip label={`📍 ${album.location}`} size="small" />
              )}
              <Chip label={album.eventType} size="small" color="primary" />
              <Chip
                label={statusDisplay.label}
                color={
                  statusDisplay.color === "success"
                    ? "success"
                    : statusDisplay.color === "warning"
                      ? "warning"
                      : "error"
                }
                size="small"
              />
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              color="primary"
              onClick={onUpload}
              startIcon={<UploadIcon />}
            >
              Upload Photos
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={onShare}
              startIcon={<ShareIcon />}
            >
              Share
            </Button>
            <Button
              variant="outlined"
              onClick={onEdit}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
            {album.status === "active" ? (
              <Button
                variant="outlined"
                color="warning"
                onClick={onArchive}
                startIcon={<ArchiveIcon />}
              >
                Archive
              </Button>
            ) : album.status === "archived" ? (
              <Button
                variant="outlined"
                color="success"
                onClick={onRestore}
                startIcon={<RestoreIcon />}
              >
                Restore
              </Button>
            ) : null}
            <Button
              variant="outlined"
              color="error"
              onClick={onDelete}
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          </Stack>
        </Stack>

        {/* Album Stats */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: "grey.50" }}>
              <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Photos
                </Typography>
                <Typography variant="h6">{album.totalPhotos || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: "grey.50" }}>
              <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Access Count
                </Typography>
                <Typography variant="h6">{album.accessCount || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: "grey.50" }}>
              <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Views
                </Typography>
                <Typography variant="h6">{album.viewCount || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: "grey.50" }}>
              <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(album.createdAt)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab icon={<PhotoIcon />} label={`Photos (${photos?.length || 0})`} />
          <Tab
            icon={<PeopleIcon />}
            label={`Access (${album.accessList?.length || 0})`}
          />
          <Tab icon={<SettingsIcon />} label="Settings" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ p: 3 }}>
        {activeTab === 0 && (
          <PhotoGrid
            photos={photos}
            onPhotoDelete={onPhotoDelete}
            onPhotoUpdate={onPhotoUpdate}
            onSetCover={onSetCover}
            allowEdit={true}
          />
        )}

        {activeTab === 1 && <AccessList accessList={album.accessList || []} />}

        {activeTab === 2 && <AlbumSettings album={album} />}
      </Box>
    </Paper>
  );
};

// AccessList Sub-component
const AccessList = ({ accessList }) => {
  if (accessList.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="text.secondary">
          No one has been given access to this album yet.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead sx={{ bgcolor: "grey.50" }}>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Access Count</TableCell>
            <TableCell>Last Accessed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accessList.map((access) => (
            <TableRow key={access.id}>
              <TableCell>{access.name}</TableCell>
              <TableCell>{access.email}</TableCell>
              <TableCell>
                <Chip
                  label={access.role}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={access.status}
                  size="small"
                  color={
                    access.status === "accepted"
                      ? "success"
                      : access.status === "pending"
                        ? "warning"
                        : "error"
                  }
                />
              </TableCell>
              <TableCell align="center">{access.accessCount || 0}</TableCell>
              <TableCell>
                {access.lastAccessed
                  ? formatDateTime(access.lastAccessed)
                  : "Never"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// AlbumSettings Sub-component
const AlbumSettings = ({ album }) => {
  const settingItems = [
    {
      label: "Allow Download",
      value: album.settings?.allowDownload ? "✅" : "❌",
    },
    {
      label: "Allow Comments",
      value: album.settings?.allowComments ? "✅" : "❌",
    },
    { label: "Watermark", value: album.settings?.watermark ? "✅" : "❌" },
    {
      label: "Password Protected",
      value: album.settings?.passwordProtected ? "✅" : "❌",
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Album Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1}>
            <Typography variant="body2">
              <strong>Code:</strong> {album.code}
            </Typography>
            <Typography variant="body2">
              <strong>Created:</strong> {formatDateTime(album.createdAt)}
            </Typography>
            <Typography variant="body2">
              <strong>Updated:</strong> {formatDateTime(album.updatedAt)}
            </Typography>
            <Typography variant="body2">
              <strong>Client:</strong> {album.clientNames}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {album.clientEmail}
            </Typography>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Permissions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1}>
            {settingItems.map((item) => (
              <Typography key={item.label} variant="body2">
                <strong>{item.label}:</strong> {item.value}
              </Typography>
            ))}
          </Stack>
        </Paper>
      </Grid>

      {album.tags && album.tags.length > 0 && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Tags
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {album.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Stack>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default AlbumManager;
